import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import '../formStyles.css';
import { RecurrenceCalculationUtils } from '../RecurrenceCalculation/RecurrenceCalculation';
import { fetchClientByCode, duplicateShipment } from '../../services/api';
import MessageBox from '../MessageBox/MessageBox';
import MySelect from '../MySelect/MySelect';
import LoadingButton from '../LoadingButton/LoadingButton';
import EmailSelector from '../EmailSelector/EmailSelector';
import IconButton from '../IconButton/IconButton';
import CommentIcon from '@mui/icons-material/Comment';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import Tooltip from '@mui/material/Tooltip';
import { requestFullScreen, exitFullScreen } from '../../utils/fullScreenUtils';
import InternalComments from '../InternalComments/InternalComments';
import FileList from '../FileList/FileList';
import DuplicationComponent from '../DuplicationComponent/DuplicationComponent';
import ClientAddressAutocomplete from '../ClientAddressAutocompleteForm/ClientAddressAutocompleteForm';
import DisputesComponent from '../DisputesComponent/DisputesComponent';
import PostcodeCityInputs from '../PostcodeCityInputs/PostcodeCityInputs';
import DateInput from '../DateInput/DateInput';
import CmrStatusModal from '../CmrStatusModal/CmrStatusModal';
import { useForm } from '../BaseForm/FormContext';
import { useAddressSuggestions } from '../../hooks/useAddressSuggestions';
import { useNavbarControl } from '../../hooks/useNavbarControl';
import { useStaticData } from '../../hooks/useStaticData';
import { useFormCalculations } from '../../hooks/useFormCalculations';
import { useAutoSaveCalculations } from '../../hooks/useAutoSaveCalculations';
import { useCarrierActions } from '../../hooks/useCarrierActions';
import { useClientActions } from '../../hooks/useClientActions';
import { useInitialDataLoad } from '../../hooks/useInitialDataLoad';
import { useFieldUpdates } from '../../hooks/useFieldUpdates';
import { useFormValidation } from '../../hooks/useFormValidation';
import { useDocumentActions } from '../../hooks/useDocumentActions';
import { useShipmentActions } from '../../hooks/useShipmentActions';
import { extractErrorMessage, createErrorMessageBoxConfig } from '../../utils/errorHandler';
import { format } from 'date-fns';
import { formatWeight, formatHour } from '../../utils/dataFormatting';
import { SHIPMENT_URLS } from '../../config/routeUrls';

export default function ShipmentForm({ setShowNavbar, setShowTabsFooter }) {
  const {
    isLoading,
    setIsLoading,
    isNew,
    setIsNew,
    formData,
    setFormData,
    messageBoxConfig,
    setMessageBoxConfig,
    showMessageBox,
    setShowMessageBox,
    handleChange,
    handleBack,
    handleFakeFormClick,
    field_names_translations,
    delay,
  } = useForm();
  const { shipment_number } = useParams();
  const navigate = useNavigate();

  const pickupNameRef = useRef(null);
  const deliveryNameRef = useRef(null);

  const {
    addressSuggestions,
    showAddressSuggestionsPickup,
    showAddressSuggestionsDelivery,
    handleAddressSuggestionsDisplay,
    handleAddressSuggestionClick,
    handleDeleteAddressSuggestion,
    skipCityAutocompleteRef,
  } = useAddressSuggestions(pickupNameRef, deliveryNameRef);
  const [showInternalComments, setShowInternalComments] = useState(false);
  const [showMailSelector, setShowMailSelector] = useState(false);
  const [isFootageManuallySet, setIsFootageManuallySet] = useState(shipment_number !== 'nouveau');
  const [emailSelectorFunction, setEmailSelectorFunction] = useState(null);
  const [showFileList, setShowFileList] = useState(false);
  const [showDuplicationComponent, setShowDuplicationComponent] = useState(false);
  const [showDisputesComponent, setShowDisputesComponent] = useState(false);
  const [showCmrStatusModal, setShowCmrStatusModal] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(!!document.fullscreenElement);

  useEffect(() => {
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  const toggleFullScreen = () => {
    if (isFullScreen) {
      exitFullScreen();
    } else {
      requestFullScreen();
    }
  };

  const createdDate = formData.entry_date
    ? `CRÉÉ LE ${format(new Date(formData.entry_date), 'dd/MM/yyyy')}`
    : '';
  const historyEvents = formData.history ? formData.history.replace(/,/g, '\n') : '';
  const formattedHistory = [createdDate, historyEvents].filter(Boolean).join('\n');

  useNavbarControl(setShowNavbar, setShowTabsFooter);

  const { clientCodes, carrierCodes } = useStaticData();
  const { carrierEmails, carrierData } = useCarrierActions(formData);

  const {
    showClientAddressAutocomplete,
    setShowClientAddressAutocomplete,
    handleConfirmClientAddressAutocomplete,
  } = useClientActions(
    formData,
    setFormData,
    setMessageBoxConfig,
    setShowMessageBox,
    isLoading,
    isNew,
    skipCityAutocompleteRef
  );

  const calculations = useFormCalculations(formData, isFootageManuallySet);
  useAutoSaveCalculations(calculations, setFormData);

  const handleFootageChange = e => {
    setIsFootageManuallySet(true);
    handleChange(e);
  };

  const handlePalletChange = e => {
    setIsFootageManuallySet(false);
    handleChange(e);
  };
  useInitialDataLoad(shipment_number, setFormData, setIsLoading, setIsNew, delay);

  const { updateField, updateForm } = useFieldUpdates(
    shipment_number,
    isNew,
    formData,
    setFormData,
    setMessageBoxConfig,
    setShowMessageBox,
    field_names_translations
  );

  const { validateFields } = useFormValidation(formData, setMessageBoxConfig, setShowMessageBox);

  const {
    displayOrderPreview,
    handleClientOrderDisplay,
    handleGenerateProformaClick,
    handleConfirmEmailSelectorToSendClientFile,
  } = useDocumentActions(
    formData,
    setMessageBoxConfig,
    setShowMessageBox,
    validateFields,
    setEmailSelectorFunction,
    setShowMailSelector
  );

  const {
    handleCreateShipment,
    handleModifyShipment,
    handleSubmit,
    handleCharterShipment,
    handleModifyShipmentWithCarrierNotice,
    handleCancelCarrier,
    handleCancelClient,
    handleDeleteClientCarrier,
    handleDeleteShipment,
    handleRestoreShipment,
    handleRequestCMRClick,
  } = useShipmentActions(
    formData,
    setFormData,
    setMessageBoxConfig,
    setShowMessageBox,
    updateField,
    updateForm,
    validateFields,
    shipment_number,
    isNew
  );

  useEffect(() => {
    if (!carrierData) {
      return;
    }

    if (carrierEmails.length === 0) {
      if (formData.carrier_email !== '') {
        setFormData(prevFormData => ({
          ...prevFormData,
          carrier_email: '',
        }));
      }
      return;
    }

    const emailNotInList = !carrierEmails.includes(formData.carrier_email);
    if (emailNotInList) {
      setFormData(prevFormData => ({
        ...prevFormData,
        carrier_email: carrierEmails[0],
      }));
    }
  }, [carrierData, carrierEmails, formData.carrier_email, setFormData]);

  const handleCancelClientClick = e => {
    if (validateFields(['client', 'carrier', 'dates', 'addresses'])) {
      setMessageBoxConfig({
        message: `Annuler le dossier ${shipment_number} au client ? Le prix client passera à 0.`,
        type: 'yesno',
        loading_button: false,
        input: false,
        onConfirm: handleCancelClient,
        onCancel: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    }
  };

  const handleWeightBlur = e => {
    const formattedValue = formatWeight(e.target.value);
    if (formattedValue !== e.target.value) {
      setFormData(prev => ({ ...prev, weight: formattedValue }));
    }
  };

  const handleHourBlur = e => {
    const { name, value } = e.target;
    const formattedValue = formatHour(value);
    if (formattedValue !== value) {
      setFormData(prev => ({ ...prev, [name]: formattedValue }));
    }
  };

  const handleTailLiftChange = e => {
    const newValue = e.target.value === 'true';
    setFormData(prev => ({
      ...prev,
      tail_lift: newValue,
      pallet_truck: newValue,
    }));
  };

  const handleNameChange = (e, prefix) => {
    handleChange(e);
    handleAddressSuggestionsDisplay(e);
    if (e.target.value === '') {
      setFormData(prev => ({
        ...prev,
        [`${prefix}_address1`]: '',
        [`${prefix}_address2`]: '',
        [`${prefix}_postcode`]: '',
        [`${prefix}_city`]: '',
        [`${prefix}_contact`]: '',
      }));
    }
  };

  const handleCharterShipmentClick = e => {
    if (carrierData && carrierData.is_blacklisted) {
      setMessageBoxConfig({
        message: `Attention : le transporteur ${formData.carrier} est blacklisté. Êtes-vous sûr de vouloir continuer l'affrètement ?`,
        type: 'yesno',
        loading_button: false,
        input: false,
        onConfirm: () => {
          setShowMessageBox(false);
          handleCharterShipment(e);
        },
        onCancel: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    } else {
      handleCharterShipment(e);
    }
  };

  const handleShowInternalComments = () => {
    setShowInternalComments(true);
  };

  const handleConfirmInternalComments = comments => {
    updateField('internal_comments', comments);
    setShowInternalComments(false);
  };

  const handleShowFileList = () => {
    setShowFileList(true);
  };

  const handleShowDuplicationComponent = () => {
    setShowDuplicationComponent(true);
  };

  const handleConfirmDuplicationComponentClick = duplication_parameters => {
    let duplicata_number_message = '1 dossier sera créé.';
    if (duplication_parameters.duplicationType === 'recurrence') {
      const total_duplicatas = RecurrenceCalculationUtils.calculateOccurrences(
        duplication_parameters.periodicity,
        duplication_parameters.daysOfWeek,
        duplication_parameters.startDateRecurrence,
        duplication_parameters.endDateRecurrence,
        duplication_parameters.numberOfOccurrences
      );
      duplicata_number_message = `${total_duplicatas} ${
        total_duplicatas > 1 ? 'dossiers seront créés.' : 'dossier sera créé.'
      }`;
    }

    setMessageBoxConfig({
      message: `Dupliquer le dossier ${formData.shipment_number} ? ${duplicata_number_message}`,
      type: 'yesno',
      loading_button: true,
      input: false,
      onConfirm: () => handleConfirmDuplicationComponent(duplication_parameters),
      onCancel: () => setShowMessageBox(false),
      onClose: () => setShowMessageBox(false),
    });
    setShowMessageBox(true);
  };

  const handleConfirmDuplicationComponent = async duplication_parameters => {
    const DuplicateShipment = async duplication_parameters => {
      try {
        const response = await duplicateShipment(formData, duplication_parameters);
        return response;
      } catch (error) {
        const errorConfig = createErrorMessageBoxConfig(error, () => setShowMessageBox(false));
        setMessageBoxConfig({
          ...errorConfig,
          message: `Une erreur est survenue lors de la duplication du dossier: ${extractErrorMessage(
            error
          )}`,
        });
        setShowMessageBox(true);
      }
    };

    const response = await DuplicateShipment(duplication_parameters);

    if (response) {
      const nb_shipments_created = response.data.nb_shipments_created;
      setMessageBoxConfig({
        message: `Le dossier a bien été dupliqué. ${nb_shipments_created} ${
          nb_shipments_created > 1 ? 'dossiers ont été créés.' : 'dossier a été créé.'
        }`,
        type: 'ok',
        loading_button: false,
        input: false,
        onConfirm: () => {
          setShowMessageBox(false);
          setShowDuplicationComponent(false);
          navigate(SHIPMENT_URLS.A_TRAITER);
        },
        onClose: () => setShowMessageBox(false),
      });
      setShowMessageBox(true);
    }
  };

  const handleCloseDisputesComponent = has_disputes => {
    setFormData({
      ...formData,
      has_disputes: has_disputes,
    });
    setShowDisputesComponent(false);
  };

  return (
    <div>
      <div className="form-style">
        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="form-header form-style-client-name">
            <div className="goBackButton">
              <button type="button" onClick={handleBack}>
                Retour
              </button>
            </div>
            <div className="form-title-container">
              {isNew ? <h1>Nouveau dossier</h1> : <h1>Dossier n° {formData.shipment_number}</h1>}
            </div>
            <div className="special-field-container semi">
              <div className="special-field form-div">
                <h3>Code client:</h3>
                <MySelect
                  values={clientCodes}
                  isCreatable={false}
                  value={
                    formData.client ? { value: formData.client, label: formData.client } : null
                  }
                  onChange={selectedOption => {
                    handleChange({
                      target: { name: 'client', value: selectedOption ? selectedOption.value : '' },
                    });
                  }}
                  validationType="str"
                  autoFocus={shipment_number === 'nouveau'}
                />
              </div>
            </div>
            {formData.carrier && carrierData && (
              <div className="carrier-info-header">
                <span className="carrier-info-name">{carrierData.name}</span>
                <span className="carrier-info-phone">{carrierData.establishment_phone1}</span>
                <span className="carrier-info-email">{formData.carrier_email}</span>
              </div>
            )}
            <div className="form-header-right-container">
              {isNew && process.env.NODE_ENV !== 'production' && (
                <button className="fake-form-fill" type="button" onClick={handleFakeFormClick}>
                  Générer un faux dossier
                </button>
              )}
              <div className="form-header-button-container">
                {!isNew && (
                  <>
                    <IconButton
                      icon={WarningAmberIcon}
                      text=""
                      onClick={() => setShowDisputesComponent(true)}
                      variant="contained"
                      styleClass={`no-background${formData.has_dispute ? ' dispute' : ''}`}
                      title="Litiges"
                    />
                    <IconButton
                      icon={CommentIcon}
                      text=""
                      onClick={handleShowInternalComments}
                      variant="contained"
                      styleClass={'no-background'}
                      title="Commentaires internes"
                    />
                    {formData.shipment_stage !== 'A TRAITER' && (
                      <IconButton
                        icon={ReceiptIcon}
                        text=""
                        onClick={handleGenerateProformaClick}
                        variant="contained"
                        styleClass="no-background"
                        title="Envoyer proforma"
                      />
                    )}
                    <IconButton
                      icon={CloudDownloadIcon}
                      text=""
                      onClick={handleShowFileList}
                      variant="contained"
                      styleClass={'no-background'}
                      title="Fichiers disponibles pour ce dossier"
                    />
                    <IconButton
                      icon={ContentCopyIcon}
                      text=""
                      onClick={handleShowDuplicationComponent}
                      variant="contained"
                      styleClass={'no-background'}
                      title="Dupliquer le dossier"
                    />
                    {formData.shipment_stage !== 'A TRAITER' && (
                      <button
                        type="button"
                        className={'no-background cmr'}
                        onClick={() => setShowCmrStatusModal(true)}
                      >
                        Statut CMR
                      </button>
                    )}
                  </>
                )}
                <IconButton
                  icon={isFullScreen ? FullscreenExitIcon : FullscreenIcon}
                  text=""
                  onClick={toggleFullScreen}
                  variant="contained"
                  styleClass={'no-background'}
                  title={isFullScreen ? 'Quitter le plein écran' : 'Plein écran'}
                />
              </div>
            </div>
          </div>
          <div className="form-style-date-enl form-div">
            <label className="date-row-label">ENLÈVEMENT :</label>
            <select name="pickup_day_when" value={formData.pickup_day_when} onChange={handleChange}>
              <option>LE</option>
              <option>A PARTIR DU</option>
            </select>
            <DateInput name="pickup_date" value={formData.pickup_date} onChange={handleChange} />
            <select
              name="pickup_time_when"
              value={formData.pickup_time_when}
              onChange={handleChange}
            >
              <option>A</option>
              <option>AVANT</option>
              <option>APRES</option>
              <option>ENTRE</option>
            </select>
            <input
              type="text"
              name="pickup_hour"
              value={formData.pickup_hour}
              onChange={handleChange}
              onBlur={handleHourBlur}
            />
          </div>
          <div className="form-style-date-liv form-div">
            <label className="date-row-label">LIVRAISON :</label>
            <select
              name="delivery_day_when"
              value={formData.delivery_day_when}
              onChange={handleChange}
            >
              <option>LE</option>
              <option>JUSQU'AU</option>
            </select>
            <DateInput
              name="delivery_date"
              value={formData.delivery_date}
              onChange={handleChange}
            />
            <select
              name="delivery_time_when"
              value={formData.delivery_time_when}
              onChange={handleChange}
            >
              <option>A</option>
              <option>AVANT</option>
              <option>APRES</option>
              <option>ENTRE</option>
            </select>
            <input
              type="text"
              name="delivery_hour"
              value={formData.delivery_hour}
              onChange={handleChange}
              onBlur={handleHourBlur}
            />
          </div>

          <div className="form-style-address-enl form-div">
            <div className="horizontal-input-group">
              <label>Nom :</label>
              <div className="route-suggestions-input-container" ref={pickupNameRef}>
                <input
                  type="text"
                  name="pickup_name"
                  value={formData.pickup_name}
                  onChange={e => handleNameChange(e, 'pickup')}
                  onFocus={handleAddressSuggestionsDisplay}
                  autoComplete="one-time-code"
                  placeholder="3 caractères min."
                />
                {showAddressSuggestionsPickup && addressSuggestions.length > 0 && (
                  <ul className="route-suggestions-container">
                    {addressSuggestions.map(addressSuggestion => (
                      <li key={addressSuggestion.id}>
                        <span
                          className="suggestion-text"
                          onClick={() =>
                            handleAddressSuggestionClick(
                              addressSuggestion,
                              'pickup',
                              formData,
                              setFormData
                            )
                          }
                        >
                          {addressSuggestion.name} - {addressSuggestion.postcode}{' '}
                          {addressSuggestion.city}
                          {addressSuggestion.contact && (
                            <>
                              {' - '}
                              <span className="suggestion-contact">
                                {addressSuggestion.contact}
                              </span>
                            </>
                          )}
                        </span>
                        <span className="suggestion-actions">
                          <Tooltip
                            title={
                              <div className="address-tooltip">
                                <div className="address-tooltip-name">{addressSuggestion.name}</div>
                                {addressSuggestion.address1 && (
                                  <div>{addressSuggestion.address1}</div>
                                )}
                                {addressSuggestion.address2 && (
                                  <div>{addressSuggestion.address2}</div>
                                )}
                                <div>
                                  {addressSuggestion.postcode} {addressSuggestion.city}
                                </div>
                                {addressSuggestion.contact && (
                                  <div className="address-tooltip-contact">
                                    {addressSuggestion.contact}
                                  </div>
                                )}
                              </div>
                            }
                            placement="left"
                            enterDelay={0}
                            leaveDelay={0}
                            arrow
                          >
                            <span className="suggestion-action-btn suggestion-info-btn">
                              <InfoOutlinedIcon fontSize="small" />
                            </span>
                          </Tooltip>
                          <span
                            className="suggestion-action-btn suggestion-delete-btn"
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteAddressSuggestion(addressSuggestion.id);
                            }}
                            title="Supprimer cette adresse"
                          >
                            <CloseIcon fontSize="small" />
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="horizontal-input-group">
              <label>Adresse :</label>
              <input
                type="text"
                name={field_names_translations['pickup_address1']}
                value={formData.pickup_address1}
                onChange={handleChange}
                autoComplete="one-time-code"
              />
            </div>
            <div className="horizontal-input-group">
              <label>Adresse 2 :</label>
              <input
                type="text"
                name={field_names_translations['pickup_address2']}
                value={formData.pickup_address2}
                onChange={handleChange}
                autoComplete="one-time-code"
              />
            </div>
            <PostcodeCityInputs
              prefix="pickup"
              postCodeFieldName={field_names_translations['pickup_postcode']}
              cityFieldName={field_names_translations['pickup_city']}
              handleChange={handleChange}
              formData={formData}
              setFormData={setFormData}
              loading={isLoading}
              skipCityAutocompleteRef={skipCityAutocompleteRef}
            />
            <div className="horizontal-input-group">
              <label>Contact enlèvement :</label>
              <input
                type="text"
                name="pickup_contact"
                value={formData.pickup_contact}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Référence enlèvement :</label>
              <input
                type="text"
                name="pickup_reference"
                value={formData.pickup_reference}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-style-address-liv form-div">
            <div className="horizontal-input-group">
              <label>Nom :</label>
              <div className="route-suggestions-input-container" ref={deliveryNameRef}>
                <input
                  type="text"
                  name="delivery_name"
                  value={formData.delivery_name}
                  onChange={e => handleNameChange(e, 'delivery')}
                  onFocus={handleAddressSuggestionsDisplay}
                  autoComplete="one-time-code"
                  placeholder="3 caractères min."
                />
                {showAddressSuggestionsDelivery && addressSuggestions.length > 0 && (
                  <ul className="route-suggestions-container">
                    {addressSuggestions.map(addressSuggestion => (
                      <li key={addressSuggestion.id}>
                        <span
                          className="suggestion-text"
                          onClick={() =>
                            handleAddressSuggestionClick(
                              addressSuggestion,
                              'delivery',
                              formData,
                              setFormData
                            )
                          }
                        >
                          {addressSuggestion.name} - {addressSuggestion.postcode}{' '}
                          {addressSuggestion.city}
                          {addressSuggestion.contact && (
                            <>
                              {' - '}
                              <span className="suggestion-contact">
                                {addressSuggestion.contact}
                              </span>
                            </>
                          )}
                        </span>
                        <span className="suggestion-actions">
                          <Tooltip
                            title={
                              <div className="address-tooltip">
                                <div className="address-tooltip-name">{addressSuggestion.name}</div>
                                {addressSuggestion.address1 && (
                                  <div>{addressSuggestion.address1}</div>
                                )}
                                {addressSuggestion.address2 && (
                                  <div>{addressSuggestion.address2}</div>
                                )}
                                <div>
                                  {addressSuggestion.postcode} {addressSuggestion.city}
                                </div>
                                {addressSuggestion.contact && (
                                  <div className="address-tooltip-contact">
                                    {addressSuggestion.contact}
                                  </div>
                                )}
                              </div>
                            }
                            placement="left"
                            enterDelay={0}
                            leaveDelay={0}
                            arrow
                          >
                            <span className="suggestion-action-btn suggestion-info-btn">
                              <InfoOutlinedIcon fontSize="small" />
                            </span>
                          </Tooltip>
                          <span
                            className="suggestion-action-btn suggestion-delete-btn"
                            onClick={e => {
                              e.stopPropagation();
                              handleDeleteAddressSuggestion(addressSuggestion.id);
                            }}
                            title="Supprimer cette adresse"
                          >
                            <CloseIcon fontSize="small" />
                          </span>
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="horizontal-input-group">
              <label>Adresse :</label>
              <input
                type="text"
                name={field_names_translations['delivery_address1']}
                value={formData.delivery_address1}
                onChange={handleChange}
                autoComplete="one-time-code"
              />
            </div>
            <div className="horizontal-input-group">
              <label>Adresse 2 :</label>
              <input
                type="text"
                name={field_names_translations['delivery_address2']}
                value={formData.delivery_address2}
                onChange={handleChange}
                autoComplete="one-time-code"
              />
            </div>
            <PostcodeCityInputs
              prefix="delivery"
              postCodeFieldName={field_names_translations['delivery_postcode']}
              cityFieldName={field_names_translations['delivery_city']}
              handleChange={handleChange}
              formData={formData}
              setFormData={setFormData}
              loading={isLoading}
              skipCityAutocompleteRef={skipCityAutocompleteRef}
            />
            <div className="horizontal-input-group">
              <label>Contact livraison :</label>
              <input
                type="text"
                name="delivery_contact"
                value={formData.delivery_contact}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group">
              <label>Référence livraison :</label>
              <input
                type="text"
                name="delivery_reference"
                value={formData.delivery_reference}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="form-style-marchandises form-div">
            <h2>Marchandises</h2>
          </div>

          <div className="form-style-vehicules form-div">
            <div className="horizontal-input-group">
              <label>Ref cde client :</label>
              <input
                type="text"
                name="shipment_reference"
                value={formData.shipment_reference}
                onChange={handleChange}
              />
            </div>
            <div className="horizontal-input-group sub-group">
              <label className="horizontal-input-group title">Palettes</label>
              <label>Nombre :</label>
              <input
                type="number"
                name="number_of_pallets"
                value={formData.number_of_pallets ?? ''}
                onChange={handlePalletChange}
              />
              <label className="supp-element">Type :</label>
              <select
                type="text"
                name="pallet_type"
                value={formData.pallet_type}
                onChange={handlePalletChange}
              >
                <option>80 x 120</option>
                <option>100 x 120</option>
                <option>120 x 120</option>
                <option>80 x 60</option>
                <option>COLIS</option>
                <option>AUTRE</option>
                <option></option>
              </select>
              <label className="supp-element">Hauteur :</label>
              <input
                type="number"
                name="pallet_height"
                value={formData.pallet_height ?? ''}
                onChange={handleChange}
              />
            </div>

            <div className="horizontal-input-group">
              <label className="horizontal-input-group title">Métrage</label>
              <input
                type="number"
                name="footage"
                value={formData.footage ?? ''}
                onChange={handleFootageChange}
              />
              <label className="horizontal-input-group title supp-element">Poids total (kg)</label>
              <input
                type="text"
                name="weight"
                value={formData.weight}
                onChange={handleChange}
                onBlur={handleWeightBlur}
              />
            </div>

            <div className="horizontal-input-group sub-group">
              <label className="horizontal-input-group title">Vehicule</label>
              <div className="vehicules-fieldset-container">
                <div>
                  <input
                    type="checkbox"
                    name="porteur_taut"
                    id="porteur_taut"
                    checked={formData.porteur_taut}
                    onChange={handleChange}
                  />
                  <label htmlFor="porteur_taut">Porteur taut</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="semi_taut"
                    id="semi_taut"
                    checked={formData.semi_taut}
                    onChange={handleChange}
                  />
                  <label htmlFor="semi_taut">Semi taut</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="semi_fourgon"
                    id="semi_fourgon"
                    checked={formData.semi_fourgon}
                    onChange={handleChange}
                  />
                  <label htmlFor="semi_fourgon">Semi fourgon</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="porteur_fourgon"
                    id="porteur_fourgon"
                    checked={formData.porteur_fourgon}
                    onChange={handleChange}
                  />
                  <label htmlFor="porteur_fourgon">Porteur fourgon</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="semi_frigo"
                    id="semi_frigo"
                    checked={formData.semi_frigo}
                    onChange={handleChange}
                  />
                  <label htmlFor="semi_frigo">Semi frigo</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="porteur_frigo"
                    id="porteur_frigo"
                    checked={formData.porteur_frigo}
                    onChange={handleChange}
                  />
                  <label htmlFor="porteur_frigo">Porteur frigo</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="vingt_metres_cube"
                    id="vingt_metres_cube"
                    checked={formData.vingt_metres_cube}
                    onChange={handleChange}
                  />
                  <label htmlFor="vingt_metres_cube">20M3</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="vingt_metres_cube_frigo"
                    id="vingt_metres_cube_frigo"
                    checked={formData.vingt_metres_cube_frigo}
                    onChange={handleChange}
                  />
                  <label htmlFor="vingt_metres_cube_frigo">20M3 frigo</label>
                </div>
                <div>
                  <input
                    type="checkbox"
                    name="plateau"
                    id="plateau"
                    checked={formData.plateau}
                    onChange={handleChange}
                  />
                  <label htmlFor="plateau">Plateau</label>
                </div>
              </div>
            </div>

            <div className="multiple-radio-inputs-grid-wrapper">
              <div className="multiple-radio-inputs-grid">
                <label>Hayon :</label>
                <div className="yes-no-radio-group">
                  <div>
                    <input
                      type="radio"
                      name="tail_lift"
                      id="tail_lift_true"
                      value="true"
                      checked={formData.tail_lift === true}
                      onChange={handleTailLiftChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="tail_lift_true">
                      Oui
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="tail_lift"
                      id="tail_lift_false"
                      value="false"
                      checked={formData.tail_lift === false}
                      onChange={handleTailLiftChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="tail_lift_false">
                      Non
                    </label>
                  </div>
                </div>
                <label>ADR :</label>
                <div className="yes-no-radio-group">
                  <div>
                    <input
                      type="radio"
                      name="adr"
                      id="adr_true"
                      value="true"
                      checked={formData.adr === true}
                      onChange={handleChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="adr_true">
                      Oui
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="adr"
                      id="adr_false"
                      value="false"
                      checked={formData.adr === false}
                      onChange={handleChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="adr_false">
                      Non
                    </label>
                  </div>
                </div>
              </div>
              <div className="multiple-radio-inputs-grid">
                <label>Transpalette :</label>
                <div className="yes-no-radio-group">
                  <div>
                    <input
                      type="radio"
                      name="pallet_truck"
                      id="pallet_truck_true"
                      value="true"
                      checked={formData.pallet_truck === true}
                      onChange={handleChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="pallet_truck_true">
                      Oui
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="pallet_truck"
                      id="pallet_truck_false"
                      value="false"
                      checked={formData.pallet_truck === false}
                      onChange={handleChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="pallet_truck_false">
                      Non
                    </label>
                  </div>
                </div>
                <label>Echange palette :</label>
                <div className="yes-no-radio-group">
                  <div>
                    <input
                      type="radio"
                      name="pallet_exchanger"
                      id="pallet_exchanger_true"
                      value="true"
                      checked={formData.pallet_exchanger === true}
                      onChange={handleChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="pallet_exchanger_true">
                      Oui
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      name="pallet_exchanger"
                      id="pallet_exchanger_false"
                      value="false"
                      checked={formData.pallet_exchanger === false}
                      onChange={handleChange}
                    />
                    <label className="yes-no-radio-group-label" htmlFor="pallet_exchanger_false">
                      Non
                    </label>
                  </div>
                  {formData.pallet_exchanger === true && (
                    <div className="exchanger-count">
                      <label htmlFor="number_of_exchangers">Nb :</label>
                      <input
                        type="number"
                        id="number_of_exchangers"
                        name="number_of_exchangers"
                        value={formData.number_of_exchangers ?? ''}
                        onChange={handleChange}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {formData.adr === true && (
              <div className="horizontal-input-group sub-group">
                <label>Class :</label>
                <select
                  type="text"
                  name="class_type"
                  value={formData.class_type}
                  onChange={handleChange}
                >
                  {[...Array(10).keys()].map(num => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
                <label className="supp-element">Code UN :</label>
                <input
                  type="text"
                  name="un_code"
                  value={formData.un_code}
                  onChange={handleChange}
                />
                <label className="supp-element">GRP :</label>
                <select type="text" name="grp" value={formData.grp} onChange={handleChange}>
                  <option>I</option>
                  <option>II</option>
                  <option>III</option>
                </select>
              </div>
            )}
          </div>

          <div className="form-style-commentaires">
            <div className="vertical-input-group">
              <label>Commentaires:</label>
              <textarea
                name="comments"
                autoComplete="off"
                value={formData.comments}
                onChange={handleChange}
              />
            </div>
            {/* <div className="horizontal-input-group carrier-due-date">
                            <label>Date d'échéance facture tps:</label>
                            <input 
                                type="date"
                                name="carrier_due_date"
                                value={formData.carrier_due_date}
                                onChange={handleChange}
                            />
                        </div> */}
          </div>

          <div className="form-style-client-price form-div">
            <div className="special-field-container form-div">
              <div className="special-field full form-div">
                <h3>Prix client:</h3>
                <input
                  type="text"
                  name="client_price"
                  className="oversized"
                  value={formData.client_price ?? ''}
                  onChange={handleChange}
                  disabled={formData.invoicing_status === 'FACTURÉ'}
                />
                <h3 className="after-input">€ (HT)</h3>
              </div>
            </div>
          </div>

          <div className="form-style-carrier-name form-div">
            <div className="special-field-container semi">
              <div className="special-field form-div">
                <h3>Nom du transporteur:</h3>
                <MySelect
                  values={carrierCodes}
                  isCreatable={false}
                  value={
                    formData.carrier ? { value: formData.carrier, label: formData.carrier } : null
                  }
                  onChange={selectedOption =>
                    handleChange({
                      target: {
                        name: 'carrier',
                        value: selectedOption ? selectedOption.value : '',
                      },
                    })
                  }
                  validationType="str"
                />
              </div>
            </div>
          </div>

          <div className="form-style-carrier-email form-div">
            <div className="horizontal-input-group">
              <label>Email transporteur :</label>
              <select name="carrier_email" value={formData.carrier_email} onChange={handleChange}>
                {carrierEmails.map((email, index) => (
                  <option key={index}>{email}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-style-carrier-price form-div">
            <div className="special-field-container semi form-div">
              <div className="special-field form-div">
                <h3>Prix transporteur:</h3>
                <input
                  type="text"
                  name="carrier_price"
                  className="oversized"
                  value={formData.carrier_price ?? ''}
                  onChange={handleChange}
                />
                <h3 className="after-input">€ (HT)</h3>
              </div>
            </div>
            <div className="special-field-container semi form-div">
              <div className="special-field margin form-div">
                <div className="margin-group form-div">
                  <h4>Tx de marge:</h4>
                  <input
                    type="text"
                    name="margin_rate"
                    className="oversized"
                    value={(formData.margin_rate * 100).toFixed(2) + '%'}
                    readOnly
                  />
                </div>
                <div className="margin-group form-div">
                  <h4 className="right-input-label">Marge:</h4>
                  <input
                    type="text"
                    name="margin"
                    className="oversized"
                    value={formData.margin.toLocaleString('fr-FR', {
                      style: 'currency',
                      currency: 'EUR',
                    })}
                    readOnly
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="form-style-history form-div">
            <p>{formattedHistory}</p>
          </div>

          <div className="form-style-endbuttons form-div">
            {formData.shipment_is_active && (
              <div className="endbuttons-container form-div">
                <div className="endbuttons-container line form-div">
                  <LoadingButton
                    onClick={displayOrderPreview}
                    className={formData.carrier === '' ? 'hidden-preserve-space' : ''}
                  >
                    Aperçu tps
                  </LoadingButton>
                  <button
                    type="button"
                    onClick={handleModifyShipmentWithCarrierNotice}
                    className={
                      isNew || formData.shipment_stage === 'A TRAITER'
                        ? 'hidden-preserve-space'
                        : ''
                    }
                  >
                    Modification transporteur
                  </button>
                  <button
                    type="button"
                    onClick={handleCancelCarrier}
                    className={
                      isNew || formData.shipment_stage === 'A TRAITER'
                        ? 'hidden-preserve-space'
                        : ''
                    }
                  >
                    Annulation transporteur
                  </button>
                  {!isNew && (
                    <button
                      className={`delete-button ${
                        isNew || formData.shipment_stage === 'A TRAITER'
                          ? 'hidden-preserve-space'
                          : ''
                      }`}
                      type="button"
                      onClick={handleDeleteClientCarrier}
                    >
                      Supprimer Tps - Cli
                    </button>
                  )}
                </div>
                <div className="endbuttons-container line form-div">
                  <LoadingButton
                    onClick={handleClientOrderDisplay}
                    className={
                      isNew || formData.shipment_stage === 'A TRAITER'
                        ? 'hidden-preserve-space'
                        : ''
                    }
                  >
                    PDF Client
                  </LoadingButton>
                  {isNew && (
                    <button
                      type="button"
                      onClick={handleCreateShipment}
                      className={!isNew ? 'hidden-preserve-space' : ''}
                    >
                      Créer
                    </button>
                  )}
                  {!isNew && (
                    <button
                      type="button"
                      onClick={handleModifyShipment}
                      className={isNew ? 'hidden-preserve-space' : ''}
                    >
                      Modification client
                    </button>
                  )}
                  {(formData.shipment_stage === 'A TRAITER' || isNew) && (
                    <button
                      type="button"
                      onClick={handleCharterShipmentClick}
                      className={
                        !isNew && formData.shipment_stage !== 'A TRAITER'
                          ? 'hidden-preserve-space'
                          : ''
                      }
                    >
                      Affréter
                    </button>
                  )}
                  {!isNew && (
                    <>
                      {formData.shipment_stage !== 'A TRAITER' && (
                        <button
                          type="button"
                          onClick={handleCancelClientClick}
                          className={
                            isNew || formData.shipment_stage === 'A TRAITER'
                              ? 'hidden-preserve-space'
                              : ''
                          }
                        >
                          Annulation client
                        </button>
                      )}
                      <button
                        className={`delete-button ${isNew ? 'hidden-preserve-space' : ''}`}
                        type="button"
                        onClick={handleDeleteShipment}
                      >
                        Supprimer définitivement
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
            {!formData.shipment_is_active && (
              <div className="endbuttons-container form-div">
                <button className="delete-button" type="button" onClick={handleRestoreShipment}>
                  Restaurer
                </button>
              </div>
            )}
          </div>
        </form>

        {showMessageBox && (
          <MessageBox
            message={messageBoxConfig.message}
            onClose={() => setShowMessageBox(false)}
            onConfirm={messageBoxConfig.onConfirm}
            onCancel={messageBoxConfig.onCancel}
            type={messageBoxConfig.type}
            loading_button={messageBoxConfig.loading_button}
            input={messageBoxConfig.input}
            technicalDetails={messageBoxConfig.technicalDetails}
          />
        )}
        {showInternalComments && (
          <InternalComments
            shipment_number={shipment_number}
            value={formData.internal_comments}
            onClose={() => setShowInternalComments(false)}
            onConfirm={handleConfirmInternalComments}
          />
        )}
        {showMailSelector && (
          <EmailSelector
            fetchFunction={fetchClientByCode}
            sendFunction={emailSelectorFunction}
            code={formData.client}
            onClose={() => setShowMailSelector(false)}
            onConfirm={handleConfirmEmailSelectorToSendClientFile}
            onCancel={() => setShowMailSelector(false)}
          />
        )}
        {showFileList && (
          <FileList
            data_type={'shipment'}
            data_identifier={shipment_number}
            onClose={() => setShowFileList(false)}
          />
        )}
        {showDuplicationComponent && (
          <DuplicationComponent
            carrierCodes={carrierCodes}
            carrier={formData.carrier}
            carrier_price={formData.carrier_price}
            carrier_emails={carrierEmails}
            carrier_email={formData.carrier_email}
            pickup_data={{
              pickup_day_when: formData.pickup_day_when,
              pickup_time_when: formData.pickup_time_when,
              pickup_hour: formData.pickup_hour,
            }}
            delivery_data={{
              delivery_day_when: formData.delivery_day_when,
              delivery_time_when: formData.delivery_time_when,
              delivery_hour: formData.delivery_hour,
            }}
            onClose={() => setShowDuplicationComponent(false)}
            onConfirm={handleConfirmDuplicationComponentClick}
          />
        )}
        {showClientAddressAutocomplete && (
          <ClientAddressAutocomplete
            onConfirm={handleConfirmClientAddressAutocomplete}
            onCancel={() => setShowClientAddressAutocomplete(false)}
          />
        )}
        {showDisputesComponent && (
          <DisputesComponent
            shipmentNumber={formData.shipment_number}
            onClose={handleCloseDisputesComponent}
          />
        )}
        {showCmrStatusModal && (
          <CmrStatusModal
            shipmentNumber={formData.shipment_number}
            formData={formData}
            carrierData={carrierData}
            handleRequestCMRClick={handleRequestCMRClick}
            onClose={() => setShowCmrStatusModal(false)}
          />
        )}
      </div>
    </div>
  );
}
